import { Link } from "react-router-dom";
import { Rocket } from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import DocsCategoryLayout from "../components/docs/DocsCategoryLayout";
import { Steps, Tip } from "../components/docs/DocsBits";

const ARTICLES = [
  {
    id: "create-account",
    title: "Create your account & set up your business profile",
    body: (
      <>
        <p>The first 5 minutes set the foundation for everything else — invoices, customer-facing pages, and the caller ID on your business calls.</p>
        <Steps
          items={[
            <>Go to <Link to="/register">/register</Link> and sign up. Email, password, optional company name. No credit card required for the 7-day free trial.</>,
            <>Once logged in, head to <strong>Settings &gt; Business</strong>.</>,
            <>Add your <strong>company name</strong> (this appears on every invoice and estimate header).</>,
            <>Add your <strong>business address</strong> — used for tax calculations on invoices and on your public customer portal.</>,
            <>Add your <strong>contact phone and email</strong> — these are the fallback numbers on customer-facing emails.</>,
            <>Upload your <strong>logo</strong>. Square or wide both work; we render it at the top of every invoice/estimate PDF.</>,
            <>Click <strong>Save</strong>. Your changes apply to all future documents immediately.</>,
          ]}
        />
        <Tip>If you operate under multiple brands, pick the one your customers see most often as your primary. Multi-brand support comes via the multi-location plan — see the Multi-Location article in Features.</Tip>
      </>
    ),
  },
  {
    id: "services",
    title: "Add your first services and job types",
    body: (
      <>
        <p>Job types are the menu of services you offer. They become quick-pick options when you create a job and a line item when you build an invoice.</p>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; Job Types</strong>.</>,
            <>Click <strong>+ New Job Type</strong>.</>,
            <>Enter a name (e.g. &quot;Lock rekey&quot;, &quot;HVAC tune-up&quot;, &quot;Garage spring replacement&quot;).</>,
            <>Set a <strong>default price</strong> and <strong>default duration</strong>. Both are editable per-job; this just pre-fills the form.</>,
            <>Optionally pick a <strong>category</strong> (Repair, Install, Maintenance, Emergency) — categories drive your reports.</>,
            <>Save. Repeat for every service you actively sell.</>,
          ]}
        />
        <Tip>Don&apos;t over-engineer this. Start with 5–10 of your most common jobs. You can add more later as you encounter unusual requests.</Tip>
      </>
    ),
  },
  {
    id: "phone-number",
    title: "Buy a phone number",
    body: (
      <>
        <p>Your dedicated business number is the caller ID your customers see, the source for your SMS templates, and the line that routes inbound calls to whatever destination you choose.</p>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; Phones</strong>.</>,
            <>Click <strong>Buy Number</strong>.</>,
            <>Search by <strong>area code</strong> (e.g. <code>415</code> for San Francisco) or <strong>city</strong>. Toll-free numbers are also available.</>,
            <>Pick a number from the list. It activates within a few seconds.</>,
            <>Choose a default routing mode: <strong>Forward</strong> (ring your cell), <strong>Voice Client</strong> (ring the in-app dialer), <strong>IVR</strong> (multi-option menu), or <strong>Voicemail</strong>.</>,
            <>If you picked Forward, enter the destination number in E.164 format (e.g. <code>+14155551234</code>).</>,
          ]}
        />
        <Tip>Buy a local number in the area code where most of your customers live. Local numbers get answered ~2× more often than 800 numbers per industry studies.</Tip>
      </>
    ),
  },
  {
    id: "invite-tech",
    title: "Invite your first technician",
    body: (
      <>
        <p>Techs get a separate, focused mobile experience — they don&apos;t need (or want) the full owner dashboard. Inviting them is a 30-second SMS flow.</p>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; Team</strong>.</>,
            <>Click <strong>+ Invite Member</strong>.</>,
            <>Enter their <strong>name</strong>, <strong>email</strong>, and <strong>phone number</strong>.</>,
            <>Pick the role: <em>Technician</em> for field staff. (Use <em>Dispatcher</em> for office staff who book jobs but don&apos;t go on-site.)</>,
            <>Click <strong>Send Invite</strong>. The tech gets an SMS with a setup link.</>,
            <>They tap the link, install the mobile app (App Store or Play Store), set a password, and they&apos;re ready to receive jobs.</>,
          ]}
        />
        <Tip>You can re-send an invitation if the original SMS expires (links are valid for 7 days). Just open the team member row and click <strong>Resend Invite</strong>.</Tip>
      </>
    ),
  },
  {
    id: "first-job",
    title: "Book your first job end-to-end",
    body: (
      <>
        <p>This is the full workflow — customer creation through paid invoice. Once you&apos;ve done one, you&apos;ll do the next 100 in a fraction of the time.</p>
        <Steps
          items={[
            <><strong>Create the customer.</strong> Go to <strong>Customers</strong> &gt; <strong>+ New Customer</strong>. Add name, phone, email, and at least one service address.</>,
            <><strong>Create the job.</strong> Go to <strong>Jobs</strong> &gt; <strong>+ New Job</strong>. Pick the customer; the address auto-fills (or you pick which one if multiple).</>,
            <><strong>Pick the service and time.</strong> Choose a job type from your saved services, then set the date and start/end time.</>,
            <><strong>Assign a technician.</strong> Pick from the team list. They get a push notification immediately.</>,
            <><strong>Send the confirmation.</strong> If the <code>job_scheduled</code> SMS template is enabled, the customer gets a confirmation text automatically.</>,
            <><strong>Tech does the job.</strong> They open the app, tap <strong>Start</strong>, do the work, attach photos if needed, then tap <strong>Complete</strong>.</>,
            <><strong>Send the invoice.</strong> From the completed job, click <strong>Convert to Invoice</strong>. Add line items if needed. Click <strong>Send</strong> — the customer gets it via email and SMS.</>,
            <><strong>Get paid.</strong> If Stripe is connected, the customer pays online via the payment link in the invoice. The invoice flips to <em>Paid</em> automatically and the <code>invoice_paid</code> SMS template fires.</>,
          ]}
        />
        <Tip>Once you&apos;ve completed your first end-to-end job, set up the SMS templates for <code>job_reminder</code>, <code>quote_sent</code>, and <code>invoice_sent</code> — these run on every future job with zero manual effort.</Tip>
      </>
    ),
  },
];

const DocsGettingStartedPage = () => (
  <DocsCategoryLayout
    category={{
      id: "getting-started",
      name: "Getting Started",
      icon: Rocket,
      intro: "Five short articles, in order, to get your account running and your first job booked. Plan for 10–15 minutes total.",
    }}
    articles={ARTICLES}
  />
);

export default DocsGettingStartedPage;
